// Global
import React, { useRef, useState } from 'react';
import NextLink from 'next/link';
import { signOut } from 'next-auth/react';

// Local
import styles from './Header.module.scss';
import { DEFAULT_LOGIN_URL } from 'lib/utils/user-profile-urls';
import accountStyles from './HeaderAccount/HeaderAccount.module.scss';
import { HeaderPrimaryLinkBar } from './HeaderPrimaryLinkBar';
import { PureHeaderPrimaryLinkBar } from './types';
import CtaLink from 'components/helpers/Atoms/Cta/CtaLink';
import { SvgIcon } from 'components/helpers/SvgIcon';
import useDictionary from 'lib/hooks/useDictionary';
import { useUserProfileUrls } from 'lib/hooks/useUserProfileUrls';
import { useRealPathName } from 'lib/hooks/sitecore/useRealPathName';
import { SearchBar } from 'components/authorable/shared/navigation/Search/SearchBar';
import { HeaderLanguageBar } from './HeaderLanguageBar/HeaderLanguageBar';
import { HeaderAccount } from './HeaderAccount/HeaderAccount';
import { MyFavourites } from './HeaderAccount/MyFavourites';
import { useSessionSafe } from 'lib/hooks/auth/useSessionSafe';

interface HeaderProps {
  logo: React.ReactNode;
  primaryLinkBar: PureHeaderPrimaryLinkBar[];
  hideUserAccountCTA?: boolean;
  hideSearchToggle?: boolean;
  hideFavouritesButton?: boolean;
  searchBarVariant?: 'default' | 'simple';

  // already present in your interface.
  hideLanguageToggle?: boolean;
  languageLinks?: { text?: string; locale: string }[];
  favouritesIcon?: React.ReactNode;
}

export const Header: React.FC<HeaderProps> = (props) => {
  const {
    logo,
    primaryLinkBar,
    hideUserAccountCTA,
    hideSearchToggle,
    hideFavouritesButton,
    searchBarVariant,
    hideLanguageToggle,
    languageLinks = [],
    favouritesIcon,
  } = props;

  const shouldShowUserAccountCTA = !(
    hideUserAccountCTA === true || String(hideUserAccountCTA) === '1'
  );

  const shouldShowFavouritesButton = !(
    hideFavouritesButton === true || String(hideFavouritesButton) === '1'
  );

  const shouldShowLanguageSwitch =
    !(hideLanguageToggle === true || String(hideLanguageToggle) === '1') &&
    languageLinks.length > 0;

  const { getDictionaryValue } = useDictionary();
  const { data: session } = useSessionSafe();

  const { loginUrl, signupUrl, manageAccountUrl, favoritesUrl } = useUserProfileUrls();
  const realPathName = useRealPathName();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  function toggleNav(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    setMobileNavOpen((prev) => !prev);
  }

  return (
    <>
      <header
        id="header"
        data-component="authorable/shared/navigation/header/header"
        className={styles.header}
        role="banner"
      >
        <HeaderLanguageBar links={languageLinks} visible={shouldShowLanguageSwitch} />

        <div className={styles.primaryLinksWrapper}>
          <div className={styles.headerUtilityBarContainer}>
            <div className={styles.headerUtilityBar}>
              <div className={styles.headerUtilityBarLogo}>
                {logo && (
                  <NextLink
                    href="/"
                    aria-label={getDictionaryValue('Navigation.Header.SiteLogo') || 'Site logo'}
                  >
                    {logo}
                  </NextLink>
                )}
              </div>
              {/* Show SearchBar on right and hide CTA links if searchBarVariant is 'simple' */}
              {searchBarVariant === 'simple' ? (
                <div className={styles.searchBarRightWrapper}>
                  <SearchBar />
                </div>
              ) : (
                <>
                  {/* Desktop search slot — placed before auth in the DOM so that on desktop
                      the tab order is: logo → search bar → sign-up → sign-in → hamburger.
                      display:none on mobile/tablet removes it from both layout and tab order. */}
                  {hideSearchToggle !== true && (
                    <div className={styles.searchBarDesktop}>
                      <SearchBar />
                    </div>
                  )}
                  {/* Auth actions */}
                  <div
                    className={`${
                      session && shouldShowUserAccountCTA
                        ? accountStyles.authWrapperLoggedIn
                        : styles.authWrapper
                    } printHide`}
                  >
                    {shouldShowUserAccountCTA && (
                      <>
                        {session ? (
                          <div className={accountStyles.accountAuthWrapper}>
                            <HeaderAccount
                              accountLabel={
                                getDictionaryValue('Navigation.Header.MyAccount') || 'My Account'
                              }
                              manageLabel={
                                getDictionaryValue('Navigation.Header.ManageAccount') || 'Manage'
                              }
                              logoutLabel={
                                getDictionaryValue('Navigation.Header.LogOut') || 'Log out'
                              }
                              manageUrl={manageAccountUrl || '/user-profile'}
                              onLogout={() => signOut({ callbackUrl: DEFAULT_LOGIN_URL })}
                            />
                            {shouldShowFavouritesButton && (
                              <MyFavourites
                                favouritesCount={session?.user?.favorites?.length ?? 0}
                                favouritesUrl={favoritesUrl || '/user-profile'}
                                label={
                                  getDictionaryValue('Navigation.Header.MyFavourites') || undefined
                                }
                                textLabel={
                                  getDictionaryValue('Navigation.Header.MyFavourites') ||
                                  'My Favourites'
                                }
                                icon={favouritesIcon}
                              />
                            )}
                          </div>
                        ) : (
                          <>
                            <CtaLink
                              text={getDictionaryValue('Navigation.Header.SignUp') || 'Sign up'}
                              ctaStyle="filled"
                              ctaComponentClass="component-site-header-color-utility-bar-button-color-1"
                              url={`${signupUrl}?returnUrl=${encodeURIComponent(realPathName || '/')}`}
                            />
                            <CtaLink
                              text={getDictionaryValue('Navigation.Header.SignIn') || 'Sign in'}
                              ctaStyle="outline"
                              ctaComponentClass="component-site-header-color-utility-bar-button-color-2"
                              url={`${loginUrl}?returnUrl=${encodeURIComponent(realPathName || '/')}`}
                            />
                          </>
                        )}
                      </>
                    )}

                    <button
                      ref={buttonRef}
                      className={`${styles.megaNavToggle}`}
                      onClick={toggleNav}
                      aria-controls="header-primary-link-bar-list"
                      aria-expanded={mobileNavOpen ? true : false}
                    >
                      <span className="screenReaderText">
                        {`${getDictionaryValue('Navigation.Header.MegaNav') || 'Mega nav'} ${
                          mobileNavOpen
                            ? getDictionaryValue('Navigation.Header.Expanded') || 'Expanded'
                            : getDictionaryValue('Navigation.Header.Collapsed') || 'Collapsed'
                        }`}
                      </span>
                      <SvgIcon
                        icon={mobileNavOpen ? 'menu-close' : 'hamburger-menu'}
                        size="md"
                        viewBox="0 0 28 28"
                      />
                    </button>
                  </div>
                  {/* Mobile/zoom the tab order is: logo → sign-up → sign-in → hamburger → search bar.
                      inert while the mobile nav is open so Tab moves into the nav instead. */}
                  {hideSearchToggle !== true && (
                    <div className={styles.searchBarMobile} inert={mobileNavOpen}>
                      <SearchBar />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {primaryLinkBar && (
            <div className={`${styles.headerPrimaryLinkBar} printHide`}>
              <HeaderPrimaryLinkBar
                mobileNavOpen={mobileNavOpen}
                primaryLinkBar={primaryLinkBar}
                setMobileNavOpen={setMobileNavOpen}
                buttonRef={buttonRef}
                linkContext="header"
              />
            </div>
          )}
        </div>
      </header>
    </>
  );
};
